import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// VehicleSchematicPage.jsx - WEBSOCKET VERSION con INMUTABILIDAD GARANTIZADA
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  DirectionsCar as VehicleIcon,
  Search as SearchIcon,
  GasMeter as ColdGaugeIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  DoNotDisturb as NoSensorIcon,
  FilterList as FilterIcon,
  VolumeOff as MuteIcon,
  VolumeUp as UnmuteIcon,
  AccessTime as HoursIcon,
  Thermostat as TemperatureIcon,
  Speed as PSIIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Movie as DemoIcon,
  GridView as CardsViewIcon,
  ViewList as ListViewIcon,
  Map as MapViewIcon
} from '@mui/icons-material';

import { useAuth } from '../hooks/useAuth';
import { useApiCall } from '../hooks/useApi';
import { api } from '../services/api';
import LoadingPage from '../components/common/LoadingPage';
import VehicleSchematicCard from '../components/schematics/VehicleSchematicCard';
import VehicleTelemetryModal from '../components/schematics/VehicleTelemetryModal/index';
import AlarmManager from '../components/schematics/AlarmManager';
import AIRecommendationsPanel from '../components/ai/AIRecommendationsPanel';
import FleetOperationalView from '../components/fleet/FleetOperationalView';
import { useAlarmMute } from '../hooks/useAlarmMute';
import { useTutorialContext } from '../contexts/TutorialContext';

import {
  useWebSocket,
  useVehiclesSubscription,
  useAlarmSubscription,
  useWebSocketEvent
} from '../hooks/useWebSocket';

const SCHEMATICS_COMPANY_SCOPE_KEY = 'tpms_schematics_company_scope';
const ALL_COMPANIES_SCOPE = 'all';
const ALL_COMPANIES_PAGE_SIZE = 24;
const SCHEMATICS_SCOPE_EVENT = 'tpms-schematics-scope-changed';
const ALL_COMPANIES_FILTER = 'all-companies';
const COLD_MODE_KEY = 'tpms_schematics_cold_mode';   // legado: solo para migrar
const DISPLAY_MODE_KEY = 'tpms_schematics_display_mode';

// El boton de metrica cicla entre los CUATRO modos. "Frio" ya no es un boton
// aparte: es el cuarto estado de este mismo control.
const DISPLAY_MODES = ['pressure', 'temperature', 'hours', 'cold'];

// Vista del panel: un solo boton que cicla esquematicos -> lista -> mapa.
const PANEL_VIEW_KEY = 'tpms_schematics_panel_view';
const PANEL_VIEWS = { CARDS: 'cards', LIST: 'list', MAP: 'map' };
const PANEL_VIEW_ORDER = [PANEL_VIEWS.CARDS, PANEL_VIEWS.LIST, PANEL_VIEWS.MAP];

const VehicleSchematicPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user, companies = [], currentCompany } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState(ALL_COMPANIES_FILTER);
  const [companyScope, setCompanyScope] = useState(
    () => localStorage.getItem(SCHEMATICS_COMPANY_SCOPE_KEY) === ALL_COMPANIES_SCOPE ? ALL_COMPANIES_SCOPE : 'current'
  );
  const [allCompaniesVehicles, setAllCompaniesVehicles] = useState([]);
  const [allCompaniesMeta, setAllCompaniesMeta] = useState({
    page: 1,
    pageSize: ALL_COMPANIES_PAGE_SIZE,
    total: 0,
    hasMore: false,
    companiesCount: 0
  });
  const [allCompaniesLoading, setAllCompaniesLoading] = useState(false);
  const [allCompaniesError, setAllCompaniesError] = useState(null);
  const [selectedVehicleForTelemetry, setSelectedVehicleForTelemetry] = useState(null);
  const { showDemoModal: demoModalOpen, setShowDemoModal: setDemoModalOpen } = useTutorialContext();

  const { isConnected, connectionId, service: wsService } = useWebSocket();

  const [vehicleDataMap, setVehicleDataMap] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [alarmStatuses, setAlarmStatuses] = useState([]);

  const [displayMode, setDisplayMode] = useState(() => {
    const saved = localStorage.getItem(DISPLAY_MODE_KEY);
    if (DISPLAY_MODES.includes(saved)) return saved;
    // Migracion del boton de frio anterior: si estaba activo, se entra en 'cold'.
    return localStorage.getItem(COLD_MODE_KEY) === '1' ? 'cold' : 'pressure';
  });
  // El modo frio ya no tiene estado propio: es uno de los estados de displayMode.
  const coldMode = displayMode === 'cold';

  const [panelView, setPanelView] = useState(
    () => Object.values(PANEL_VIEWS).includes(localStorage.getItem(PANEL_VIEW_KEY))
      ? localStorage.getItem(PANEL_VIEW_KEY)
      : PANEL_VIEWS.CARDS
  );

  // Modo "Presión en frío": el esquemático muestra el PSI equivalente en frío
  // según la temperatura del sensor y el ajuste necesario contra el ideal del
  // perfil. El cálculo lo hace el backend (motor central), no el navegador.
  const [coldData, setColdData] = useState({});
  const [coldLoading, setColdLoading] = useState(false);
  const [coldNote, setColdNote] = useState('');

  // Silencio compartido y persistido (mismo estado en toda la app).
  const {
    globalMute,
    vehicleMutes,
    toggleGlobalMute: toggleGlobalMuteState,
    toggleVehicleMute: toggleVehicleMuteState,
    clearVehicleMutes,
  } = useAlarmMute(currentCompany?.company_id || currentCompany?.id || null);
  const [alarmState, setAlarmState] = useState({
    totalAlarms: 0,
    criticalAlarms: 0,
    warningAlarms: 0,
    unacknowledgedAlarms: 0,
    globalMute: false,
    mutedVehicles: 0
  });

  // Keys internas para filtros de estado (nunca traducciones).
  // Las traducciones se aplican solo al mostrar (getStatusLabel).
  const STATUS_KEYS = { NORMAL: 'normal', ALARM: 'alarm', NO_DATA: 'no_data', NO_SENSOR: 'no_sensor' };
  const [positionStatusFilters, setPositionStatusFilters] = useState([
    STATUS_KEYS.NORMAL, STATUS_KEYS.ALARM, STATUS_KEYS.NO_DATA, STATUS_KEYS.NO_SENSOR
  ]);

  const getStatusLabel = useCallback((statusKey) => {
    switch (statusKey) {
      case STATUS_KEYS.NO_DATA: return t('hardcoded.components_schematics_positiontile.no-data');
      case STATUS_KEYS.NO_SENSOR: return t('hardcoded.components_schematics_positiontile.no-sensor');
      case STATUS_KEYS.NORMAL: return 'normal';
      case STATUS_KEYS.ALARM: return 'alarm';
      default: return statusKey;
    }
  }, [t]);

  const {
    data: companyVehicles,
    loading: vehiclesLoading,
    refetch: refetchCompanyVehicles
  } = useApiCall(api.vehicles.getAll);

  const isAllCompaniesView = companyScope === ALL_COMPANIES_SCOPE;
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    const syncScopeFromStorage = () => {
      const nextScope = localStorage.getItem(SCHEMATICS_COMPANY_SCOPE_KEY) === ALL_COMPANIES_SCOPE
        ? ALL_COMPANIES_SCOPE
        : 'current';
      setCompanyScope(nextScope);
    };

    window.addEventListener(SCHEMATICS_SCOPE_EVENT, syncScopeFromStorage);
    window.addEventListener('storage', syncScopeFromStorage);

    return () => {
      window.removeEventListener(SCHEMATICS_SCOPE_EVENT, syncScopeFromStorage);
      window.removeEventListener('storage', syncScopeFromStorage);
    };
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const loadAllCompaniesVehicles = useCallback(async (page = 1, append = false) => {
    setAllCompaniesLoading(true);
    setAllCompaniesError(null);

    try {
      const response = await api.vehicles.getAllCompanies({
        page,
        page_size: ALL_COMPANIES_PAGE_SIZE,
        search: debouncedSearchTerm || undefined
      });

      const payload = response?.data || {};
      const nextItems = Array.isArray(payload.items) ? payload.items : [];

      setAllCompaniesVehicles(prev => {
        const base = append ? [...prev, ...nextItems] : nextItems;
        const seen = new Set();
        return base.filter(vehicle => {
          if (!vehicle?.id || seen.has(vehicle.id)) return false;
          seen.add(vehicle.id);
          return true;
        });
      });

      setAllCompaniesMeta({
        page: payload.page || page,
        pageSize: payload.page_size || ALL_COMPANIES_PAGE_SIZE,
        total: payload.total || 0,
        hasMore: Boolean(payload.has_more),
        companiesCount: payload.companies_count || 0
      });
    } catch (error) {
      console.error('❌ Error loading all-companies schematics:', error);
      setAllCompaniesError(t('hardcoded.pages_vehicleschematicpage.no_fue_posible_cargar_la'));
      if (!append) {
        setAllCompaniesVehicles([]);
      }
    } finally {
      setAllCompaniesLoading(false);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (isAllCompaniesView) {
      loadAllCompaniesVehicles(1, false);
    }
  }, [isAllCompaniesView, loadAllCompaniesVehicles]);

  const activeVehicles = useMemo(
    () => (isAllCompaniesView ? allCompaniesVehicles : (companyVehicles || [])),
    [isAllCompaniesView, allCompaniesVehicles, companyVehicles]
  );

  const availableCompaniesInAllView = useMemo(() => {
    if (!isAllCompaniesView) return [];

    const companyMap = new Map();
    allCompaniesVehicles.forEach((vehicle) => {
      if (vehicle?.company_id && vehicle?.company_name && !companyMap.has(vehicle.company_id)) {
        companyMap.set(vehicle.company_id, {
          company_id: vehicle.company_id,
          company_name: vehicle.company_name
        });
      }
    });

    return Array.from(companyMap.values()).sort((a, b) => a.company_name.localeCompare(b.company_name));
  }, [isAllCompaniesView, allCompaniesVehicles]);

  useEffect(() => {
    if (!isAllCompaniesView) {
      setSelectedCompanyFilter(ALL_COMPANIES_FILTER);
      return;
    }

    if (
      selectedCompanyFilter !== ALL_COMPANIES_FILTER &&
      !availableCompaniesInAllView.some(company => company.company_id === selectedCompanyFilter)
    ) {
      setSelectedCompanyFilter(ALL_COMPANIES_FILTER);
    }
  }, [isAllCompaniesView, availableCompaniesInAllView, selectedCompanyFilter]);

  const vehicleIds = useMemo(() => {
    if (!activeVehicles) return [];

    const ids = [];
    activeVehicles.forEach(vehicle => {
      ids.push(vehicle.id);
      if (vehicle.child_vehicles) {
        vehicle.child_vehicles.forEach(child => ids.push(child.id));
      }
    });
    return ids;
  }, [activeVehicles]);

  const { alarms: wsAlarms, activeAlarmCount } = useAlarmSubscription();

  useVehiclesSubscription(isAllCompaniesView ? [] : vehicleIds);

  // Re-subscribe to all vehicles whenever the WebSocket connects or reconnects
  useEffect(() => {
    if (!isAllCompaniesView && isConnected && vehicleIds.length > 0) {
      if (isDev) {
        console.log(t('hardcoded.pages_vehicleschematicpage.ws_conectadoreconectado_-_re-suscribiendo_a'), vehicleIds.length, t('vehicles.title'));
      }
      wsService.subscribeToVehicles(vehicleIds.map(String));
    }
  }, [isConnected, isAllCompaniesView, vehicleIds, wsService, isDev]);

  useWebSocketEvent('alarm_status_update', useCallback((data) => {
    if (data.statuses) {
      setAlarmStatuses(data.statuses);
    }
  }, []));

  useWebSocketEvent('telemetry_update', useCallback((data) => {
    if (!data?.vehicle_id || data.position === undefined || data.position === null) {
      if (isDev) {
        console.warn(t('hardcoded.pages_vehicleschematicpage.telemetry_update_datos_invalidos_o_incompletos'), data);
      }
      return;
    }

    const vehicleId = String(data.vehicle_id);
    const positionKey = String(data.position);
    if (isDev) {
      console.log(t('hardcoded.pages_vehicleschematicpage.telemetry_update_recibido_vehiculo_vehicleid_posicion', { vehicleId: vehicleId, positionKey: positionKey, pressure: data.pressure }));
    }

    setVehicleDataMap(prev => {
      const currentVehicle = prev[vehicleId] || {
        realtime_data: {},
        historical_data: {},
        stats: {}
      };

      return {
        ...prev,
        [vehicleId]: {
          ...currentVehicle,
          realtime_data: {
            ...(currentVehicle.realtime_data || {}),
            [positionKey]: {
              pressure: data.pressure,
              temperature: data.temperature,
              timestamp: data.timestamp,
              dataAge: data.dataAge,
              sensor_id: data.sensor_id,
              isRealtime: true,
              _updateTime: Date.now()
            }
          },
          _lastUpdate: Date.now()
        }
      };
    });

    setLastRefresh(Date.now());
  }, [isDev]));

  useWebSocketEvent('telemetry_batch', useCallback((batchData) => {
    if (!batchData || typeof batchData !== 'object') return;
    const vehicleCount = Object.keys(batchData).length;
    if (isDev) {
      console.log(t('hardcoded.pages_vehicleschematicpage.telemetry_batch_recibido_vehiclecount_vehiculos', { vehicleCount: vehicleCount }));
    }

    setVehicleDataMap(prev => {
      const newMap = { ...prev };

      Object.entries(batchData).forEach(([vehicleIdRaw, positions]) => {
        const vehicleId = String(vehicleIdRaw);
        const currentVehicle = newMap[vehicleId] || {
          realtime_data: {},
          historical_data: {},
          stats: {}
        };

        const updatedRealtime = { ...(currentVehicle.realtime_data || {}) };

        Object.entries(positions || {}).forEach(([position, posData]) => {
          updatedRealtime[String(position)] = {
            ...posData,
            isRealtime: true,
            _updateTime: Date.now()
          };
        });

        newMap[vehicleId] = {
          ...currentVehicle,
          realtime_data: updatedRealtime,
          _lastUpdate: Date.now()
        };
      });

      return newMap;
    });

    setLastRefresh(Date.now());
  }, [isDev]));

  useWebSocketEvent('vehicle_data_update', useCallback((data) => {
    if (!data?.vehicle_id) return;
    const vehicleId = String(data.vehicle_id);
    if (isDev) {
      console.log(t('hardcoded.pages_vehicleschematicpage.vehicle_data_update_recibido_vehiculo_vehicleid', { vehicleId: vehicleId }));
    }
    const normalizedRealtimeData = {};
    Object.entries(data.realtime_data || {}).forEach(([position, posData]) => {
      normalizedRealtimeData[String(position)] = {
        ...posData,
        isRealtime: true,
        _updateTime: Date.now()
      };
    });

    const normalizedHistoricalData = {};
    Object.entries(data.historical_data || {}).forEach(([position, posData]) => {
      normalizedHistoricalData[String(position)] = posData;
    });

    setVehicleDataMap(prev => {
      const currentVehicle = prev[vehicleId] || {
        realtime_data: {},
        historical_data: {},
        stats: {}
      };

      return {
        ...prev,
        [vehicleId]: {
          ...currentVehicle,
          realtime_data: {
            ...(currentVehicle.realtime_data || {}),
            ...normalizedRealtimeData
          },
          historical_data: {
            ...(currentVehicle.historical_data || {}),
            ...normalizedHistoricalData
          },
          stats: {
            ...(currentVehicle.stats || {}),
            ...(data.stats || {})
          },
          _lastUpdate: Date.now()
        }
      };
    });

    setLastRefresh(Date.now());
  }, [isDev]));

  // Actualizar alarmas WebSocket
  useEffect(() => {
    if (wsAlarms && wsAlarms.length > 0) {
      if (isDev) {
        console.log(t('hardcoded.pages_vehicleschematicpage.websocket_alarms_update'), wsAlarms.length, t('hardcoded.pages_helppage.alarms'));
      }

      const formattedAlarms = wsAlarms.map(alarm => ({
        vehicle_id: alarm.vehicle_id,
        position: alarm.position,
        axle: alarm.axle,
        metric_type: alarm.metric_type,
        current_value: alarm.current_value,
        threshold_min: alarm.threshold_min,
        threshold_max: alarm.threshold_max,
        status: alarm.status || 'CRITICAL',
        unit: alarm.unit || 'PSI',
        last_updated: alarm.triggered_at || new Date().toISOString()
      }));

      setAlarmStatuses(prev => {
        const merged = [...prev];

        formattedAlarms.forEach(newAlarm => {
          const existingIndex = merged.findIndex(a =>
            a.vehicle_id === newAlarm.vehicle_id &&
            a.position === newAlarm.position &&
            a.metric_type === newAlarm.metric_type
          );

          if (existingIndex >= 0) {
            merged[existingIndex] = newAlarm;
          } else {
            merged.push(newAlarm);
          }
        });

        return merged;
      });
    }
  }, [wsAlarms, isDev]);

  // Carga inicial
  const fetchInitialData = useCallback(async () => {
    if (!activeVehicles || activeVehicles.length === 0) return;

    setIsLoadingData(true);

    try {
      const vehicleIdsStr = vehicleIds.join(',');
      const [telemetryResponse, alarmResponse] = await Promise.all([
        isAllCompaniesView
          ? api.telemetry.getLatestMultipleVehiclesAllCompanies(vehicleIdsStr)
          : api.telemetry.getLatestMultipleVehicles(vehicleIdsStr),
        isAllCompaniesView
          ? api.alarms.getStatusAllCompanies()
          : api.alarms.getStatus()
      ]);

      if (telemetryResponse.data && telemetryResponse.data.vehicles) {
        const newVehicleDataMap = {};

        Object.entries(telemetryResponse.data.vehicles).forEach(([vehicleId, vehicleData]) => {
          newVehicleDataMap[vehicleId] = {
            realtime_data: vehicleData.realtime_data || {},
            historical_data: vehicleData.historical_data || {},
            vehicle_info: vehicleData.vehicle,
            stats: {
              total_positions: vehicleData.total_positions || 0,
              realtime_positions: vehicleData.realtime_positions || 0,
              historical_positions: vehicleData.historical_positions || 0
            }
          };
        });

        setVehicleDataMap(newVehicleDataMap);
      }

      if (alarmResponse.data) {
        setAlarmStatuses(alarmResponse.data);
      }

      setLastRefresh(Date.now());
    } catch (error) {
      console.error(t('hardcoded.pages_vehicleschematicpage.error_fetching_initial_data'), error);
    } finally {
      setIsLoadingData(false);
    }
  }, [activeVehicles, isAllCompaniesView, vehicleIds]);

  useEffect(() => {
    if (activeVehicles && activeVehicles.length > 0) {
      fetchInitialData();
    }
  }, [activeVehicles, fetchInitialData]);

  // Un solo boton para la metrica: PSI -> Temperatura -> Horas -> Frio.
  const handleToggleDisplayMode = () => {
    setDisplayMode(prev => {
      const next = DISPLAY_MODES[(DISPLAY_MODES.indexOf(prev) + 1) % DISPLAY_MODES.length];
      localStorage.setItem(DISPLAY_MODE_KEY, next);
      return next;
    });
  };

  const getDisplayModeIcon = () => {
    switch (displayMode) {
      case 'temperature':
        return <TemperatureIcon />;
      case 'hours':
        return <HoursIcon />;
      case 'cold':
        return <ColdGaugeIcon />;
      default:
        return <PSIIcon />;
    }
  };

  // Color del boton segun la metrica activa (el frio usa el tono informativo).
  const displayModeColor = () => {
    switch (displayMode) {
      case 'temperature': return 'warning';
      case 'hours': return 'secondary';
      case 'cold': return 'info';
      default: return 'primary';
    }
  };

  const getDisplayModeTooltip = () => {
    switch (displayMode) {
      case 'pressure':
        return t('hardcoded.pages_vehicleschematicpage.cambiar_a_temperatura');
      case 'temperature':
        return t('hardcoded.pages_vehicleschematicpage.cambiar_a_horas');
      case 'hours':
        return t('hardcoded.pages_vehicleschematicpage.cambiar_a_frio');
      case 'cold':
        return t('hardcoded.pages_vehicleschematicpage.cambiar_a_presion');
      default:
        return t('hardcoded.pages_vehicleschematicpage.cambiar_modo_de_visualizacion');
    }
  };

  // Silenciar es SOLO sonido: no toca el estado de ningun ticket.
  const handleToggleGlobalMute = useCallback(() => {
    toggleGlobalMuteState();
  }, [toggleGlobalMuteState]);

  const handleToggleVehicleMute = useCallback((vehicleId) => {
    toggleVehicleMuteState(vehicleId);
  }, [toggleVehicleMuteState]);

  const handleUnmuteAllVehicles = useCallback(() => {
    clearVehicleMutes();
  }, [clearVehicleMutes]);

  const handleAlarmStateChange = useCallback((newAlarmState) => {
    setAlarmState(newAlarmState);
  }, []);

  const alarmStatusByVehicleAndPosition = useMemo(() => {
    return alarmStatuses.reduce((acc, alarmStatus) => {
      const vehicleId = String(alarmStatus.vehicle_id);
      const position = String(alarmStatus.position);

      if (!acc[vehicleId]) {
        acc[vehicleId] = {};
      }

      if (!acc[vehicleId][position]) {
        acc[vehicleId][position] = [];
      }

      acc[vehicleId][position].push(alarmStatus);
      return acc;
    }, {});
  }, [alarmStatuses]);

  const alarmsByVehicle = useMemo(() => {
    return alarmStatuses.reduce((acc, alarmStatus) => {
      const vehicleId = String(alarmStatus.vehicle_id);
      if (!acc[vehicleId]) {
        acc[vehicleId] = [];
      }
      acc[vehicleId].push(alarmStatus);
      return acc;
    }, {});
  }, [alarmStatuses]);

  const criticalAlarmVehicles = useMemo(() => {
    const criticalSet = new Set();
    alarmStatuses.forEach((alarmStatus) => {
      if (alarmStatus.status === 'CRITICAL') {
        criticalSet.add(String(alarmStatus.vehicle_id));
      }
    });
    return criticalSet;
  }, [alarmStatuses]);

  const handleLoadMoreAllCompanies = useCallback(() => {
    if (!allCompaniesMeta.hasMore || allCompaniesLoading) return;
    loadAllCompaniesVehicles(allCompaniesMeta.page + 1, true);
  }, [allCompaniesMeta, allCompaniesLoading, loadAllCompaniesVehicles]);

  const getPositionStatus = useCallback((vehicle, position) => {
    const assignedSensor = vehicle?.vehicle_sensors?.find(vs => vs.position === position);
    if (!assignedSensor) return STATUS_KEYS.NO_SENSOR;

    const vehicleAlarmMap = alarmStatusByVehicleAndPosition[String(vehicle.id)] || {};
    const positionAlarmStatuses = vehicleAlarmMap[String(position)] || [];

    const hasCriticalAlarm = positionAlarmStatuses.some(alarmStatus =>
      alarmStatus.status === 'CRITICAL'
    );

    if (hasCriticalAlarm) {
      return STATUS_KEYS.ALARM;
    }

    const vehicleData = vehicleDataMap[vehicle.id];
    if (!vehicleData) return STATUS_KEYS.NO_DATA;

    const realtimeData = vehicleData.realtime_data[position];
    const historicalData = vehicleData.historical_data[position];
    const posData = realtimeData || historicalData;

    if (!posData) return STATUS_KEYS.NO_DATA;

    const hasAnyData = posData.pressure !== null && posData.pressure !== undefined ||
      posData.temperature !== null && posData.temperature !== undefined ||
      posData.acceleration !== null && posData.acceleration !== undefined;

    if (!hasAnyData) return STATUS_KEYS.NO_DATA;

    if (realtimeData) {
      return STATUS_KEYS.NORMAL;
    }

    return STATUS_KEYS.NO_DATA;
  }, [alarmStatusByVehicleAndPosition, vehicleDataMap, STATUS_KEYS]);

  const shouldShowVehicle = useCallback((vehicle) => {
    if (!vehicle?.vehicle_sensors || vehicle.vehicle_sensors.length === 0) {
      return positionStatusFilters.includes(STATUS_KEYS.NO_SENSOR);
    }

    const positionStatuses = vehicle.vehicle_sensors.map(vs =>
      getPositionStatus(vehicle, vs.position)
    );

    const filters = {
      normal: positionStatusFilters.includes(STATUS_KEYS.NORMAL),
      alarm: positionStatusFilters.includes(STATUS_KEYS.ALARM),
      noData: positionStatusFilters.includes(STATUS_KEYS.NO_DATA),
      noSensor: positionStatusFilters.includes(STATUS_KEYS.NO_SENSOR)
    };

    const allNormal = positionStatuses.every(status => status === STATUS_KEYS.NORMAL);
    const hasAlarm = positionStatuses.some(status => status === STATUS_KEYS.ALARM);
    const allNoData = positionStatuses.every(status => status === STATUS_KEYS.NO_DATA);
    const allNoSensor = positionStatuses.every(status => status === STATUS_KEYS.NO_SENSOR);

    if (allNormal) return filters.normal;
    if (hasAlarm) return filters.alarm;
    if (allNoData) return filters.noData;
    if (allNoSensor) return filters.noSensor;

    const hasMatchingStatus = positionStatuses.some(status => {
      if (status === STATUS_KEYS.NORMAL && filters.normal) return true;
      if (status === STATUS_KEYS.ALARM && filters.alarm) return true;
      if (status === STATUS_KEYS.NO_DATA && filters.noData) return true;
      if (status === STATUS_KEYS.NO_SENSOR && filters.noSensor) return true;
      return false;
    });

    return hasMatchingStatus;
  }, [positionStatusFilters, getPositionStatus, STATUS_KEYS]);

  const getVehicleOverallStatus = useCallback((vehicle) => {
    if (!vehicle.vehicle_sensors || vehicle.vehicle_sensors.length === 0) {
      return STATUS_KEYS.NO_SENSOR;
    }

    if (criticalAlarmVehicles.has(String(vehicle.id))) {
      return STATUS_KEYS.ALARM;
    }

    const vehicleData = vehicleDataMap[vehicle.id];
    if (!vehicleData) return STATUS_KEYS.NO_SENSOR;

    const realtimePositions = Object.keys(vehicleData.realtime_data || {}).length;
    const historicalPositions = Object.keys(vehicleData.historical_data || {}).length;

    if (realtimePositions > 0) {
      return STATUS_KEYS.NORMAL;
    } else if (historicalPositions > 0) {
      return 'warning';
    }

    return STATUS_KEYS.NO_DATA;
  }, [criticalAlarmVehicles, vehicleDataMap, STATUS_KEYS]);

  const flattenAndSortVehicles = (vehiclesFromAPI) => {
    if (!vehiclesFromAPI) return [];

    const allVehicles = [];

    vehiclesFromAPI.forEach(vehicle => {
      allVehicles.push(vehicle);

      if (vehicle.child_vehicles && vehicle.child_vehicles.length > 0) {
        vehicle.child_vehicles.forEach(child => {
          allVehicles.push(child);
        });
      }
    });

    const sortedVehicles = [];
    const processedIds = new Set();

    allVehicles.forEach(vehicle => {
      if (processedIds.has(vehicle.id)) return;

      if (!vehicle.parent_vehicle_id) {
        sortedVehicles.push(vehicle);
        processedIds.add(vehicle.id);

        const children = allVehicles.filter(v =>
          v.parent_vehicle_id === vehicle.id && !processedIds.has(v.id)
        );

        children.forEach(child => {
          sortedVehicles.push(child);
          processedIds.add(child.id);
        });
      }
    });

    return sortedVehicles;
  };

  const allFlattenedVehicles = useMemo(
    () => flattenAndSortVehicles(activeVehicles),
    [activeVehicles]
  );

  const filteredVehicles = useMemo(() => {
    return allFlattenedVehicles.filter(vehicle => {
      if (!vehicle) return false;

      if (isAllCompaniesView && selectedCompanyFilter !== ALL_COMPANIES_FILTER) {
        if (vehicle.company_id !== selectedCompanyFilter) {
          return false;
        }
      }

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = (
          (vehicle.alias && vehicle.alias.toLowerCase().includes(searchLower)) ||
          (vehicle.make && vehicle.make.toLowerCase().includes(searchLower)) ||
          (vehicle.model && vehicle.model.toLowerCase().includes(searchLower)) ||
          (vehicle.display_name && vehicle.display_name.toLowerCase().includes(searchLower)) ||
          (vehicle.company_name && vehicle.company_name.toLowerCase().includes(searchLower))
        );
        if (!matchesSearch) return false;
      }

      return shouldShowVehicle(vehicle);
    }) || [];
  }, [allFlattenedVehicles, isAllCompaniesView, selectedCompanyFilter, searchTerm, shouldShowVehicle]);

  // Presión en frío de los vehículos visibles. Solo se pide cuando el modo está
  // activo; el backend la calcula con el mismo motor que los correos.
  const visibleVehicleIds = useMemo(
    () => filteredVehicles.map(v => v?.id).filter(Boolean).join(','),
    [filteredVehicles]
  );

  useEffect(() => {
    if (!coldMode || !visibleVehicleIds) {
      return undefined;
    }
    let cancelled = false;
    setColdLoading(true);
    api.reports.getColdPressure(visibleVehicleIds)
      .then(res => {
        if (cancelled) return;
        setColdData(res.data?.vehicles || {});
        setColdNote(res.data?.cold_note || '');
      })
      .catch(() => {
        // Se CONSERVA el cálculo anterior: vaciarlo dejaba el esquemático en
        // blanco por un fallo puntual de red. Se avisa en la nota, sin bloquear.
        if (!cancelled) setColdNote('No se pudo actualizar el cálculo en frío; se muestra el último disponible.');
      })
      .finally(() => {
        if (!cancelled) setColdLoading(false);
      });
    return () => { cancelled = true; };
  }, [coldMode, visibleVehicleIds, lastRefresh]);

  const positionStatistics = useMemo(() => {
    let normal = 0;
    let alarm = 0;
    let noData = 0;
    let noSensor = 0;

    allFlattenedVehicles.forEach(vehicle => {
      if (vehicle?.vehicle_sensors) {
        vehicle.vehicle_sensors.forEach(vs => {
          const status = getPositionStatus(vehicle, vs.position);
          switch (status) {
            case STATUS_KEYS.NORMAL:
              normal++;
              break;
            case STATUS_KEYS.ALARM:
              alarm++;
              break;
            case STATUS_KEYS.NO_DATA:
              noData++;
              break;
            case STATUS_KEYS.NO_SENSOR:
              noSensor++;
              break;
            default:
              break;
          }
        });
      }
    });

    return { normal, alarm, noData, noSensor };
  }, [allFlattenedVehicles, getPositionStatus, STATUS_KEYS]);

  const handleStatusFilterChange = useCallback((event, newFilters) => {
    if (newFilters.length > 0) {
      if (isDev) {
        console.log(t('hardcoded.pages_vehicleschematicpage.filter_change'), newFilters);
      }
      setPositionStatusFilters(newFilters);
    }
  }, [isDev, t]);

  const handleRefresh = useCallback(() => {
    if (isDev) {
      console.log(t('hardcoded.pages_vehicleschematicpage.manual_refresh_triggered'));
    }
    if (isAllCompaniesView) {
      loadAllCompaniesVehicles(1, false);
    } else {
      refetchCompanyVehicles();
    }
    fetchInitialData();
  }, [isAllCompaniesView, loadAllCompaniesVehicles, refetchCompanyVehicles, fetchInitialData, isDev]);

  // El boton de refresco manual se retiro del toolbar (los datos llegan por
  // WebSocket y por el poll automatico). La capacidad se conserva y se usa al
  // entrar al modo frio, para calcularlo sobre lecturas recien sincronizadas.
  useEffect(() => {
    if (displayMode === 'cold') handleRefresh();
    // Solo al ENTRAR en frio, no en cada refresco.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMode]);

  // Un solo boton de vista: avanza al siguiente modo y vuelve al primero.
  const handleCyclePanelView = useCallback(() => {
    setPanelView((prev) => {
      const next = PANEL_VIEW_ORDER[(PANEL_VIEW_ORDER.indexOf(prev) + 1) % PANEL_VIEW_ORDER.length];
      localStorage.setItem(PANEL_VIEW_KEY, next);
      return next;
    });
  }, []);

  // El tooltip anuncia a DONDE lleva el boton, no donde estas: es un ciclo.
  const panelViewMeta = useMemo(() => {
    switch (panelView) {
      case PANEL_VIEWS.LIST:
        return { icon: <MapViewIcon fontSize="small" />, tooltip: t('hardcoded.pages_vehicleschematicpage.vista_mapa') };
      case PANEL_VIEWS.MAP:
        return { icon: <CardsViewIcon fontSize="small" />, tooltip: t('hardcoded.pages_vehicleschematicpage.vista_tarjetas') };
      default:
        return { icon: <ListViewIcon fontSize="small" />, tooltip: t('hardcoded.pages_vehicleschematicpage.vista_lista') };
    }
  }, [panelView, t]);

  const { normal, alarm, noData, noSensor } = positionStatistics;

  // Pantalla de carga SOLO cuando todavía no hay nada que enseñar. Antes bastaba
  // con `vehiclesLoading`, y como entrar en modo frío dispara un refresco, el
  // grid entero se desmontaba y aparecía "Loading vehicle schematics…" en cada
  // cambio de modo. Los datos previos siguen en memoria durante el refetch
  // (`useApiCall` no los limpia), así que se mantiene la vista y el recálculo
  // ocurre en segundo plano.
  const hasVehiclesToRender = activeVehicles.length > 0;
  const isInitialVehiclesLoad = isAllCompaniesView ? allCompaniesLoading : vehiclesLoading;

  if (isInitialVehiclesLoad && !hasVehiclesToRender) {
    return <LoadingPage message={t('common.loading')} />;
  }

  return (
    <Container maxWidth={false} disableGutters>
      <AlarmManager
        realtimeData={{}}
        alarmStatuses={alarmStatuses}
        vehicles={activeVehicles}
        onAlarmStateChange={handleAlarmStateChange}
        globalMute={globalMute}
        vehicleMutes={vehicleMutes}
        onToggleGlobalMute={handleToggleGlobalMute}
        onToggleVehicleMute={handleToggleVehicleMute}
      />

      {allCompaniesError && isAllCompaniesView && (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
          <Typography variant="body2" color="error.main">{allCompaniesError}</Typography>
        </Paper>
      )}

      {!isAllCompaniesView && <AIRecommendationsPanel module="dashboard" title={t('hardcoded.pages_vehicleschematicpage.prioridades_del_dia_ia')} />}

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 2,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <TextField
            placeholder={t('hardcoded.pages_vehicleschematicpage.search_vehicles')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ width: { xs: 250, sm: 300 } }}
          />

          {isAllCompaniesView && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="filter-company-label">{i18n.t('navbar.company')}</InputLabel>
              <Select
                labelId="filter-company-label"
                value={selectedCompanyFilter}
                label={t('navbar.company')}
                onChange={(event) => setSelectedCompanyFilter(event.target.value)}
              >
                <MenuItem value={ALL_COMPANIES_FILTER}>{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                {availableCompaniesInAllView.map((company) => (
                  <MenuItem key={company.company_id} value={company.company_id}>
                    {company.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {isAllCompaniesView && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${filteredVehicles.length} / ${allCompaniesMeta.total} vehículos`}
            />
          )}

          <Box sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            {/* <Tooltip title={isConnected ? `WebSocket conectado (${connectionId?.slice(-8) || 'Live'})` : 'WebSocket desconectado - reconectando...'}>
              <IconButton
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  padding: 0,
                  border: '2px solid',
                  borderColor: isConnected ? 'success.main' : 'error.main',
                  borderRadius: '6px',
                  bgcolor: isConnected ? 'success.light' : 'error.light',
                  color: isConnected ? 'success.main' : 'error.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  cursor: 'default'
                }}
              >
                {isConnected ? <WifiIcon /> : <WifiOffIcon />}
              </IconButton>
            </Tooltip> */}

            <Tooltip title={globalMute ? t('hardcoded.pages_vehicleschematicpage.activar_sonido_de_todas_las') : t('hardcoded.pages_vehicleschematicpage.silenciar_todas_las_alarmas')}>
              <IconButton
                onClick={handleToggleGlobalMute}
                size="small"
                sx={{
                  width: 40,
                  height: 40,
                  padding: 0,
                  border: '2px solid',
                  borderColor: globalMute ? 'error.main' : 'primary.main',
                  borderRadius: '6px',
                  bgcolor: globalMute ? 'error.light' : 'transparent',
                  color: globalMute ? 'error.main' : 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: globalMute ? 'error.main' : 'primary.light',
                    color: globalMute ? 'white' : 'primary.dark',
                  }
                }}
              >
                {globalMute ? <MuteIcon /> : <UnmuteIcon />}
              </IconButton>
            </Tooltip>

            <Tooltip title={getDisplayModeTooltip()}>
              <Button
                variant={displayMode !== 'pressure' ? "contained" : "outlined"}
                size="small"
                onClick={handleToggleDisplayMode}
                startIcon={getDisplayModeIcon()}
                sx={{
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  padding: 0,
                  borderRadius: '6px',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  border: '2px solid',
                  borderColor: `${displayModeColor()}.main`,
                  color: displayMode !== 'pressure' ? 'white' : 'primary.main',
                  bgcolor: displayMode === 'pressure' ? 'transparent' : `${displayModeColor()}.main`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: displayMode === 'pressure' ? 'primary.light' : `${displayModeColor()}.dark`,
                    borderColor: displayMode === 'pressure' ? 'primary.main' : `${displayModeColor()}.dark`,
                    color: displayMode !== 'pressure' ? 'white' : 'primary.dark',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: 0,
                    margin: 0
                  }
                }}
              >
              </Button>
            </Tooltip>

            {/* Un solo boton de vista: esquematicos -> lista operativa -> mapa GPS. */}
            <Tooltip title={panelViewMeta.tooltip}>
              <Button
                variant={panelView === PANEL_VIEWS.CARDS ? 'outlined' : 'contained'}
                size="small"
                onClick={handleCyclePanelView}
                startIcon={panelViewMeta.icon}
                aria-label={panelViewMeta.tooltip}
                sx={{
                  width: 40,
                  height: 40,
                  minWidth: 40,
                  padding: 0,
                  borderRadius: '6px',
                  border: '2px solid',
                  borderColor: 'primary.main',
                  color: panelView === PANEL_VIEWS.CARDS ? 'primary.main' : 'white',
                  bgcolor: panelView === PANEL_VIEWS.CARDS ? 'transparent' : 'primary.main',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: panelView === PANEL_VIEWS.CARDS ? 'primary.light' : 'primary.dark',
                    borderColor: 'primary.dark',
                  },
                  '& .MuiButton-startIcon': { margin: 0 },
                }}
              >
              </Button>
            </Tooltip>

            {user?.email?.endsWith(t('hardcoded.components_common_sidebar.arsenalmx')) && (
              <Tooltip title={t('hardcoded.pages_vehicleschematicpage.demo_de_alarmas')}>
                <IconButton
                  onClick={() => setDemoModalOpen(true)}
                  size="small"
                  sx={{
                    width: 40,
                    height: 40,
                    padding: 0,
                    border: '2px solid',
                    borderColor: 'warning.main',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: 'warning.main',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'warning.main',
                      color: 'white',
                    }
                  }}
                >
                  <DemoIcon />
                </IconButton>
              </Tooltip>
            )}

            <ToggleButtonGroup
              value={positionStatusFilters}
              onChange={handleStatusFilterChange}
              size="small"
              sx={{
                display: 'flex',
                gap: 0.5,
                '& .MuiToggleButton-root': {
                  width: 40,
                  height: 40,
                  border: '2px solid',
                  borderRadius: '6px !important',
                  margin: '0 !important',
                  padding: '0 !important',
                  boxSizing: 'border-box',
                  position: 'relative',
                  transform: 'none !important',
                  transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  '&.Mui-selected': {
                    '&[data-color="success"]': {
                      backgroundColor: '#2e7d32 !important',
                      borderColor: '#2e7d32 !important',
                      color: '#ffffff !important',
                      '&:hover': {
                        backgroundColor: '#1b5e20 !important',
                        borderColor: '#1b5e20 !important',
                      }
                    },
                    '&[data-color="error"]': {
                      backgroundColor: '#d32f2f !important',
                      borderColor: '#d32f2f !important',
                      color: '#ffffff !important',
                      '&:hover': {
                        backgroundColor: '#c62828 !important',
                        borderColor: '#c62828 !important',
                      }
                    },
                    '&[data-color="light-gray"]': {
                      backgroundColor: '#9e9e9e !important',
                      borderColor: '#9e9e9e !important',
                      color: '#ffffff !important',
                      '&:hover': {
                        backgroundColor: '#757575 !important',
                        borderColor: '#757575 !important',
                      }
                    },
                    '&[data-color="standard"]': {
                      backgroundColor: '#616161 !important',
                      borderColor: '#616161 !important',
                      color: '#ffffff !important',
                      '&:hover': {
                        backgroundColor: '#424242 !important',
                        borderColor: '#424242 !important',
                      }
                    }
                  },

                  '&:not(.Mui-selected)': {
                    '&[data-color="success"]': {
                      backgroundColor: '#e8f5e8 !important',
                      borderColor: '#2e7d32 !important',
                      color: '#2e7d32 !important',
                      '&:hover': {
                        backgroundColor: '#c8e6c9 !important',
                      }
                    },
                    '&[data-color="error"]': {
                      backgroundColor: '#ffebee !important',
                      borderColor: '#d32f2f !important',
                      color: '#d32f2f !important',
                      '&:hover': {
                        backgroundColor: '#ffcdd2 !important',
                      }
                    },
                    '&[data-color="light-gray"]': {
                      backgroundColor: '#f5f5f5 !important',
                      borderColor: '#9e9e9e !important',
                      color: '#9e9e9e !important',
                      '&:hover': {
                        backgroundColor: '#eeeeee !important',
                      }
                    },
                    '&[data-color="standard"]': {
                      backgroundColor: '#f5f5f5 !important',
                      borderColor: '#616161 !important',
                      color: '#616161 !important',
                      '&:hover': {
                        backgroundColor: '#e0e0e0 !important',
                      }
                    }
                  }
                }
              }}
            >
              <Tooltip title={t('hardcoded.pages_vehicleschematicpage.tooltip_normal', { normal: normal })}>
                <ToggleButton value={STATUS_KEYS.NORMAL} data-color="success" />
              </Tooltip>
              <Tooltip title={t('hardcoded.pages_vehicleschematicpage.alarm_alarm_-_vehiculos_con', { alarm: alarm })}>
                <ToggleButton value={STATUS_KEYS.ALARM} data-color="error" />
              </Tooltip>
              <Tooltip title={t('hardcoded.pages_vehicleschematicpage.tooltip_nodata', { noData: noData })}>
                <ToggleButton value={STATUS_KEYS.NO_DATA} data-color="light-gray" />
              </Tooltip>
              <Tooltip title={t('hardcoded.pages_vehicleschematicpage.tooltip_nosensor', { noSensor: noSensor })}>
                <ToggleButton value={STATUS_KEYS.NO_SENSOR} data-color="standard" />
              </Tooltip>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* {wsLastUpdate && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Live: {wsLastUpdate.toLocaleTimeString()}
          </Typography>
        )} */}
      </Box>

      {/* Vista lista / mapa: el backend calcula estado, movilidad y tabla por
          llanta; el grid de esquematicos se conserva intacto como vista base. */}
      {panelView !== PANEL_VIEWS.CARDS && (
        <FleetOperationalView
          viewMode={panelView === PANEL_VIEWS.MAP ? 'map' : 'split'}
          searchTerm={debouncedSearchTerm || searchTerm}
          refreshKey={lastRefresh}
        />
      )}

      {panelView === PANEL_VIEWS.CARDS && (
      <Grid
        data-tutorial-id="schematic-area"
        container
        spacing={{ xs: 0.75, sm: 1, md: 1.5 }}
        sx={{
          width: '100%',
          margin: 0,
          '& .MuiGrid-item': {
            paddingLeft: { xs: '6px !important', sm: '8px !important', md: '12px !important' },
            paddingTop: { xs: '6px !important', sm: '8px !important', md: '12px !important' }
          }
        }}
      >
        {filteredVehicles.map((vehicle) => {
          const isChildVehicle = vehicle.parent_id;
          const vehicleData = vehicleDataMap[vehicle.id] || {};

          return (
            <Grid
              item
              xs={6}
              sm="auto"
              key={vehicle.id}
              sx={{
                ...(isChildVehicle && {
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 8,
                    top: -2,
                    width: 2,
                    height: 8,
                    backgroundColor: 'primary.main',
                    opacity: 0.5,
                    zIndex: 1
                  }
                })
              }}
            >
              <VehicleSchematicCard
                vehicle={vehicle}
                realtimeData={vehicleData.realtime_data || {}}
                lastAvailableData={vehicleData.historical_data || {}}
                alarms={alarmsByVehicle[vehicle.id] || []}
                alarmStatuses={alarmStatuses}
                onTelemetryClick={(_v, position) => setSelectedVehicleForTelemetry({ vehicle, position })}
                globalMute={globalMute}
                vehicleMutes={vehicleMutes}
                onToggleVehicleMute={handleToggleVehicleMute}
                isLoadingData={isLoadingData}
                lastRefresh={lastRefresh}
                displayMode={displayMode}
                showCompanyTag={isAllCompaniesView}
                coldMode={coldMode}
                coldPlan={coldData[vehicle.id]}
              />
            </Grid>
          )
        })}

        {filteredVehicles.length === 0 && !vehiclesLoading && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <VehicleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {isAllCompaniesView && companies.length === 0 ? t('hardcoded.pages_vehicleschematicpage.sin_empresas_disponibles') : t('hardcoded.components_dashboard_vehiclestatusgrid.no_vehicles_found')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAllCompaniesView && companies.length === 0
                  ? t('hardcoded.pages_vehicleschematicpage.tu_usuario_no_tiene_acceso')
                  : (searchTerm
                    ? t('hardcoded.pages_vehicleschematicpage.try_adjusting_your_search_criteria')
                    : 'Try adjusting your filters or add vehicles to see their schematics here')}
              </Typography>
            </Paper>
          </Grid>
        )}

        {isAllCompaniesView && filteredVehicles.length > 0 && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMoreAllCompanies}
                disabled={!allCompaniesMeta.hasMore || allCompaniesLoading}
              >
                {allCompaniesLoading ? t('common.loading') : allCompaniesMeta.hasMore ? t('hardcoded.pages_vehicleschematicpage.cargar_mas') : t('hardcoded.pages_vehicleschematicpage.sin_mas_resultados')}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
      )}

      <VehicleTelemetryModal
        open={!!selectedVehicleForTelemetry}
        vehicle={selectedVehicleForTelemetry?.vehicle}
        initialPosition={selectedVehicleForTelemetry?.position}
        onClose={() => setSelectedVehicleForTelemetry(null)}
        realtimeData={vehicleDataMap[selectedVehicleForTelemetry?.vehicle?.id]?.realtime_data || {}}
        lastAvailableData={vehicleDataMap[selectedVehicleForTelemetry?.vehicle?.id]?.historical_data || {}}
        alarmStatuses={alarmStatuses}
      />

      {/* AlarmDemoModal rendered globally via GlobalDemoModal in App.js */}
    </Container>
  );
};

export default VehicleSchematicPage;